import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { generateMockParcels, generateMockAssemblages } from '../services/mockData';
import { useToast } from './ToastContext';

const ParcelContext = createContext();

const initialState = {
  parcels: [],
  filteredParcels: [],
  assemblages: [],
  favorites: [],
  selectedParcel: null,
  filters: {
    state: '',
    county: '',
    futureLandUse: '',
    futureLandUseMulti: [], // Array for multiple FLU selections (assemblage filters)
    inPlaceZoning: '',
    minAcreage: '',
    maxAcreage: '',
    maxAssessedValuePerAcre: '',
    filterType: 'single', // 'single' or 'multi'
    filterMode: 'futureLandUse' // 'futureLandUse' or 'inPlaceZoning'
  },
  loading: false,
  loadingParcelDetails: false,
  error: null,
  states: [],
  counties: [],
  futureLandUseOptions: [],
  inPlaceZoningOptions: [],
  searchSummary: null,
  totalCount: 0,
  tilesetUrl: '',
  // New fields for query runtime exceeded handling
  queryRuntimeExceeded: null,
  pendingSearchWithBbox: null,
  // Fields for async filter query tracking
  filterQueryInProgress: false,
  currentFilterTaskId: null,
  // Field for query rejection due to estimated runtime
  queryRejected: null,
};

const parcelReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_LOADING_PARCEL_DETAILS':
      return { ...state, loadingParcelDetails: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_PARCELS':
      return { ...state, parcels: action.payload };
    
    case 'SET_FILTERED_PARCELS':
      return { ...state, filteredParcels: action.payload };
    
    case 'SET_ASSEMBLAGES':
      return { ...state, assemblages: action.payload };
    
    case 'SET_SELECTED_PARCEL':
      return { ...state, selectedParcel: action.payload };
    
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    case 'SET_STATES':
      return { ...state, states: action.payload };
    
    case 'SET_COUNTIES':
      return { ...state, counties: action.payload };
    
    case 'SET_FUTURE_LAND_USE_OPTIONS':
      return { ...state, futureLandUseOptions: action.payload };
    
    case 'SET_IN_PLACE_ZONING_OPTIONS':
      return { ...state, inPlaceZoningOptions: action.payload };
    
    case 'SET_SEARCH_SUMMARY':
      return { ...state, searchSummary: action.payload };
    
    case 'SET_TOTAL_COUNT':
      return { ...state, totalCount: action.payload };
    
    case 'SET_TILESET_URL':
      return { ...state, tilesetUrl: action.payload };
    
    case 'SET_QUERY_RUNTIME_EXCEEDED':
      return { ...state, queryRuntimeExceeded: action.payload };
    
    case 'SET_PENDING_SEARCH_WITH_BBOX':
      return { ...state, pendingSearchWithBbox: action.payload };
    
    case 'SET_FILTER_QUERY_IN_PROGRESS':
      return { ...state, filterQueryInProgress: action.payload };
    
    case 'SET_CURRENT_FILTER_TASK_ID':
      return { ...state, currentFilterTaskId: action.payload };
    
    case 'SET_QUERY_REJECTED':
      return { ...state, queryRejected: action.payload };
    
    case 'ADD_TO_FAVORITES':
      return { 
        ...state, 
        favorites: [...state.favorites, action.payload]
      };
    
    case 'REMOVE_FROM_FAVORITES':
      return { 
        ...state, 
        favorites: state.favorites.filter(item => item.id !== action.payload)
      };
    
    case 'CLEAR_FAVORITES':
      return { ...state, favorites: [] };
    
    case 'SET_FAVORITES':
      return { ...state, favorites: action.payload };
    
    default:
      return state;
  }
};

export const ParcelProvider = ({ children }) => {
  const [state, dispatch] = useReducer(parcelReducer, initialState);
  const { addToast, removeToast, updateProgressToast } = useToast();
  const pollingIntervalRef = useRef(null);



  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load counties when state changes
  useEffect(() => {
    if (state.filters.state) {
      loadCounties(state.filters.state);
    }
  }, [state.filters.state]);

  // Load land use options when county changes
  useEffect(() => {
    if (state.filters.county) {
      loadLandUseOptions();
    }
  }, [state.filters.county]);

  useEffect(() => {
    if (state.favorites.length === 0) {
      // Add 2 mock parcels and 1 mock assemblage as favorites
      const mockParcels = generateMockParcels({}).slice(0, 2).map(p => ({ ...p, type: 'parcel', id: p.id }));
      const mockAssemblage = generateMockAssemblages({}).slice(0, 1).map(a => ({ ...a, type: 'assemblage', id: a.id }));
      dispatch({ type: 'SET_FAVORITES', payload: [...mockParcels, ...mockAssemblage] });
    }
  }, []);

  const loadStates = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const states = await api.getStates();
      dispatch({ type: 'SET_STATES', payload: states });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load states' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadCounties = async (state) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const counties = await api.getCountiesByState(state);
      dispatch({ type: 'SET_COUNTIES', payload: counties });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load counties' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadLandUseOptions = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const [fluOptions, zoningOptions] = await Promise.all([
        api.getFutureLandUse(state.filters.state, state.filters.county),
        api.getInPlaceZoning(state.filters.state, state.filters.county)
      ]);
      
      dispatch({ type: 'SET_FUTURE_LAND_USE_OPTIONS', payload: fluOptions });
      dispatch({ type: 'SET_IN_PLACE_ZONING_OPTIONS', payload: zoningOptions });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load land use options' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Helper function to transform parcel/assemblage data
  const transformParcelData = (parcels, filterType) => {
    if (filterType === 'single') {
      return parcels.map(parcel => ({
        parcel_id: parcel.parcel_id,
        land_acres: Math.round(parseFloat(parcel.land_acres) * 100) / 100,
        land_use_category: parcel.land_use_category,
        zoning: parcel.zoning,
        addr: parcel.addr,
        owner: parcel.own1_last + ' ' + parcel.own1_first,
        assd_imp: Math.round(parseFloat(parcel.assd_imp) * 100) / 100,
        geometry: parcel.geometry,
        center: parcel.center
      }));
    } else {
      return parcels.map(assemblage => ({
        cluster_id: assemblage.cluster_id || `assemblage-${Math.random()}`,
        parcel_ids: Array.isArray(assemblage.parcel_ids) ? assemblage.parcel_ids : [],
        total_acres: Math.round(parseFloat(assemblage.total_acres) * 100) / 100 || 0,
        numberOfParcels: assemblage.chunk_size || 0,
        land_use_category: 'Assemblage',
        zoning: 'Assemblage',
        addr: 'Assemblage',
        owner: 'Assemblage',
        assd_imp: Math.round(parseFloat(assemblage.total_imp_val) * 100) / 100 || 0,
        geometry: assemblage.geometry,
        center: assemblage.center
      }));
    }
  };

  // Store toast ID for progress updates
  const filterToastIdRef = useRef(null);
  
  // Poll for filter results
  const pollFilterResults = useCallback(async (taskId) => {
    try {
      const status = await api.checkParcelFilterStatus(taskId);
      
      // Update toast with progress
      if (filterToastIdRef.current && status.progress_percentage !== undefined) {
        // Update progress toast message
        const progressMessage = status.message || status.step || `Searching parcels... ${status.progress_percentage || 0}%`;
        updateProgressToast(
          filterToastIdRef.current,
          'info',
          'Parcel Search in Progress',
          progressMessage,
          status.progress_percentage || 0
        );
      }
      
      // Check if task is complete
      if (status.status === 'SUCCESS') {
        // Check if this is actually a QueryRuntimeExceededError (status SUCCESS but error field present)
        if (status.error === 'QueryRuntimeExceededError') {
          console.log('QueryRuntimeExceededError detected in SUCCESS response:', status);
          
          // Clear polling interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Remove progress toast
          if (filterToastIdRef.current) {
            removeToast(filterToastIdRef.current);
            filterToastIdRef.current = null;
          }
          
          // Reset query state
          dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
          dispatch({ type: 'SET_CURRENT_FILTER_TASK_ID', payload: null });
          dispatch({ type: 'SET_LOADING', payload: false });
          
          // Show query rejection modal with suggestions
          dispatch({
            type: 'SET_QUERY_REJECTED',
            payload: {
              message: status.message || 'Query rejected due to estimated runtime exceeding acceptable limit.',
              error: status.error,
              suggestions: status.suggestions || []
            }
          });
          
          return true; // Task completed with rejection
        }
        
        // Normal success case - check if we have actual results
        if (!status.parcels || status.parcels.length === 0) {
          // No results but no error - might be empty result
          console.log('Query completed with no results');
        }
        
        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Remove progress toast
        if (filterToastIdRef.current) {
          removeToast(filterToastIdRef.current);
          filterToastIdRef.current = null;
        }
        
        // Reset query state
        dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
        dispatch({ type: 'SET_CURRENT_FILTER_TASK_ID', payload: null });
        dispatch({ type: 'SET_LOADING', payload: false });
        
        // Transform and set results
        const parcels = status.parcels || [];
        const transformedData = transformParcelData(parcels, state.filters.filterType);
        
        // Update search summary and total count
        dispatch({ type: 'SET_SEARCH_SUMMARY', payload: status.filter_summary || {} });
        dispatch({ type: 'SET_TOTAL_COUNT', payload: status.total_count || 0 });
        
        if (state.filters.filterType === 'single') {
          dispatch({ type: 'SET_FILTERED_PARCELS', payload: transformedData });
          dispatch({ type: 'SET_ASSEMBLAGES', payload: [] });
        } else {
          dispatch({ type: 'SET_FILTERED_PARCELS', payload: [] });
          dispatch({ type: 'SET_ASSEMBLAGES', payload: transformedData });
        }
        
        // Show success toast
        addToast('success', 'Parcel Search Complete', `Found ${status.total_count || 0} parcels matching your criteria.`, 5000);
        
        return true; // Task completed
      } else if (status.status === 'FAILURE') {
        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Remove progress toast
        if (filterToastIdRef.current) {
          removeToast(filterToastIdRef.current);
          filterToastIdRef.current = null;
        }
        
        // Reset query state
        dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
        dispatch({ type: 'SET_CURRENT_FILTER_TASK_ID', payload: null });
        dispatch({ type: 'SET_LOADING', payload: false });
        
        // Show error
        const errorMessage = status.error || status.message || 'Query failed';
        addToast('error', 'Parcel Search Failed', errorMessage, 8000);
        
        // Check if this is a query runtime exceeded error
        if (errorMessage.includes('timeout') || errorMessage.includes('exceeded')) {
          let suggestions = status.suggestions || [];
          dispatch({ 
            type: 'SET_QUERY_RUNTIME_EXCEEDED', 
            payload: {
              message: errorMessage,
              suggestions: suggestions
            }
          });
        } else {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to search parcels' });
        }
        
        return true; // Task completed (with error)
      }
      
      return false; // Task still in progress
    } catch (error) {
      console.error('Error polling filter results:', error);
      // Don't stop polling on error, just log it
      return false;
    }
  }, [state.filters.filterType, addToast, removeToast, updateProgressToast]);

  const searchParcels = async (bbox = null) => {
    // Prevent concurrent queries
    if (state.filterQueryInProgress) {
      console.log('Filter query already in progress, ignoring new request');
      return;
    }
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_QUERY_RUNTIME_EXCEEDED', payload: null });
      dispatch({ type: 'SET_QUERY_REJECTED', payload: null });
      dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: true });
      
      // Start async filter task
      const result = await api.filterParcelsAsync({
        ...state.filters,
        includeAssemblages: state.filters.filterType === 'multi',
        bbox: bbox
      });
      
      // Check if the query was rejected due to estimated runtime
      // Handle both cases: success: false in response, or error field, or status SUCCESS with error
      console.log('Filter result:', result);
      if ((result.success === false && result.error === 'QueryRuntimeExceededError') || 
          (result.error === 'QueryRuntimeExceededError') ||
          (result.status === 'SUCCESS' && result.error === 'QueryRuntimeExceededError')) {
        console.log('Query rejected due to estimated runtime - showing modal:', result);
        
        // Reset query state
        dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
        dispatch({ type: 'SET_LOADING', payload: false });
        
        // Show query rejection modal with suggestions
        dispatch({
          type: 'SET_QUERY_REJECTED',
          payload: {
            message: result.message || 'Query rejected due to estimated runtime exceeding acceptable limit.',
            error: result.error || 'QueryRuntimeExceededError',
            suggestions: result.suggestions || []
          }
        });
        
        console.log('Query rejection modal dispatched');
        return; // Exit early, don't start polling
      }
      
      // Check if we got a task ID (background task)
      if (result.task_id) {
        console.log('Background filter task started with ID:', result.task_id);
        
        // Store task ID
        dispatch({ type: 'SET_CURRENT_FILTER_TASK_ID', payload: result.task_id });
        
        // Create progress toast
        const capitalizeName = (name) => {
          if (!name) return '';
          return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
        };
        
        const countyName = capitalizeName(state.filters.county);
        const stateName = capitalizeName(state.filters.state);
        
        // Show progress toast
        filterToastIdRef.current = addToast('info', 'Parcel Search in Progress', result.message || `Searching parcels in ${countyName}, ${stateName}...`, 0, 0);
        
        // Start polling every 5 seconds
        const startPolling = () => {
          // Clear any existing interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          
          // Poll immediately, then every 5 seconds
          pollFilterResults(result.task_id).then(completed => {
            if (!completed) {
              pollingIntervalRef.current = setInterval(async () => {
                const completed = await pollFilterResults(result.task_id);
                if (completed && pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              }, 5000);
            }
          });
        };
        
        // Start polling after a short delay to allow the task to initialize
        setTimeout(startPolling, 1000);
      } else {
        // Immediate completion (shouldn't happen with async endpoint, but handle it)
        dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
        dispatch({ type: 'SET_LOADING', payload: false });
        addToast('error', 'Unexpected Response', 'Received unexpected response from server.', 5000);
      }
    } catch (error) {
      console.error('Search error:', error);
      
      // Clear polling interval if it exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Remove progress toast if it exists
      if (filterToastIdRef.current) {
        removeToast(filterToastIdRef.current);
        filterToastIdRef.current = null;
      }
      
      // Reset query state
      dispatch({ type: 'SET_FILTER_QUERY_IN_PROGRESS', payload: false });
      dispatch({ type: 'SET_CURRENT_FILTER_TASK_ID', payload: null });
      dispatch({ type: 'SET_LOADING', payload: false });
      
      // Check if this is a QueryRuntimeExceededError (query rejected before starting)
      // Check multiple places where the error might be stored
      const errorMessage = error.message || '';
      const errorResponse = error.response || {};
      const hasQueryRuntimeError = 
        errorMessage.includes('QueryRuntimeExceededError') ||
        errorResponse.error === 'QueryRuntimeExceededError';
      
      if (hasQueryRuntimeError) {
        console.log('QueryRuntimeExceededError detected in catch block:', error);
        console.log('Error response:', errorResponse);
        
        // Extract the rejection message
        let rejectionMessage = errorResponse.message || 
                              errorResponse.detail || 
                              'Query rejected due to estimated runtime exceeding acceptable limit.';
        
        // If message is a JSON string, try to parse it
        if (typeof rejectionMessage === 'string' && rejectionMessage.includes('{')) {
          try {
            const parsed = JSON.parse(rejectionMessage);
            if (parsed.message) {
              rejectionMessage = parsed.message;
            }
          } catch (parseError) {
            // If parsing fails, try to extract message from string
            const messageMatch = rejectionMessage.match(/message["\s]*:["\s]*([^"]+)/);
            if (messageMatch) {
              rejectionMessage = messageMatch[1];
            }
          }
        }
        
        // Show query rejection modal
        dispatch({
          type: 'SET_QUERY_REJECTED',
          payload: {
            message: rejectionMessage,
            error: 'QueryRuntimeExceededError'
          }
        });
        
        return; // Exit early
      }
      // Check if this is a query runtime exceeded error during execution (for multi-parcel queries)
      else if (errorMessage.includes('Query runtime exceeded') && state.filters.filterType === 'multi') {
        // Parse the error to extract suggestions
        let suggestions = [];
        try {
          const errorMatch = errorMessage.match(/"suggestions":\s*\[(.*?)\]/s);
          if (errorMatch) {
            suggestions = JSON.parse(`[${errorMatch[1]}]`);
          }
        } catch (parseError) {
          console.warn('Could not parse error suggestions:', parseError);
        }
        
        dispatch({ 
          type: 'SET_QUERY_RUNTIME_EXCEEDED', 
          payload: {
            message: errorMessage,
            suggestions: suggestions
          }
        });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to search parcels' });
        addToast('error', 'Parcel Search Failed', errorMessage || 'Failed to start parcel search.', 8000);
      }
    }
  };

  // Function to retry search with current map bounds
  const retrySearchWithBbox = async (bbox) => {
    // Prevent concurrent queries
    if (state.filterQueryInProgress) {
      console.log('Filter query already in progress, ignoring retry request');
      return;
    }
    
    dispatch({ type: 'SET_PENDING_SEARCH_WITH_BBOX', payload: bbox });
    await searchParcels(bbox);
    dispatch({ type: 'SET_PENDING_SEARCH_WITH_BBOX', payload: null });
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Function to cancel the query runtime exceeded modal
  const cancelQueryRuntimeExceeded = () => {
    dispatch({ type: 'SET_QUERY_RUNTIME_EXCEEDED', payload: null });
  };
  
  // Function to cancel the query rejection modal
  const cancelQueryRejected = () => {
    dispatch({ type: 'SET_QUERY_REJECTED', payload: null });
  };

  const addToFavorites = (item) => {
    dispatch({ type: 'ADD_TO_FAVORITES', payload: item });
  };

  const removeFromFavorites = (id) => {
    dispatch({ type: 'REMOVE_FROM_FAVORITES', payload: id });
  };

  const clearFavorites = () => {
    dispatch({ type: 'CLEAR_FAVORITES' });
  };

  const updateFilters = (filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const setSelectedParcel = (parcel) => {
    dispatch({ type: 'SET_SELECTED_PARCEL', payload: parcel });
  };

  const loadParcelDetails = async (parcelId) => {
    try {
      dispatch({ type: 'SET_LOADING_PARCEL_DETAILS', payload: true });
      
      // Get state and county from current filters
      const currentState = state.filters.state || 'ca';
      const county = state.filters.county || 'orange';
      
      const result = await api.getParcelDetails(parcelId, currentState, county);
      
      if (result.success && result.found && result.parcel) {
        // Set the detailed parcel information as selected
        const selectedParcelData = {
          ...result.parcel,
          type: 'parcel',
          parcel_id: result.parcel.parcel_id,
          land_acres: result.parcel.land_acres || result.parcel.total_acres || 0,
          land_use_category: result.parcel.land_use_category || 'N/A',
          zoning: result.parcel.zoning || 'N/A',
          addr: result.parcel.addr || 'N/A',
          owner: result.parcel.owner || 'N/A',
          assd_imp: result.parcel.assd_imp || 0
        };

        dispatch({ type: 'SET_SELECTED_PARCEL', payload: selectedParcelData });
        return selectedParcelData;
      } else {
        console.warn('No detailed parcel information found for:', parcelId);
        return null;
      }
    } catch (error) {
      console.error('Error loading parcel details:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load parcel details' });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING_PARCEL_DETAILS', payload: false });
    }
  };

  const setTilesetUrl = useCallback((url) => {
    dispatch({ type: 'SET_TILESET_URL', payload: url });
  }, []);

  const value = {
    ...state,
    searchParcels,
    retrySearchWithBbox,
    cancelQueryRuntimeExceeded,
    cancelQueryRejected,
    addToFavorites,
    removeFromFavorites,
    clearFavorites,
    updateFilters,
    setSelectedParcel,
    loadParcelDetails,
    loadStates,
    loadCounties,
    loadLandUseOptions,
    tilesetUrl: state.tilesetUrl,
    setTilesetUrl
  };

  return (
    <ParcelContext.Provider value={value}>
      {children}
    </ParcelContext.Provider>
  );
};

export const useParcelContext = () => {
  const context = useContext(ParcelContext);
  if (!context) {
    throw new Error('useParcelContext must be used within a ParcelProvider');
  }
  return context;
}; 